import React, { useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import {
  CapaBypassComplaintType,
  CapaErrorCode,
  CapaQuotationBypassMessage,
  CapaQuotationChatMessage,
  useDeleteQuotationChatMessageMutation,
  useUpdateBypassChatComplaintMutation
} from '@generated/graphql';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import BypassDialog from '@components/Chat/BypassDialog';
import errorMessage from '@utils/errorMessage';

const useStyles = makeStyles(() => {
  return {
    root: {
      width: 230,
      borderRadius: 10,
      backgroundColor: colorSet.gray000
    },
    content: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      padding: '16px'
    },
    wordBreak: {
      wordBreak: 'break-all'
    },
    buttonContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16px'
    }
  };
});

interface Props {
  message?: CapaQuotationChatMessage;
}

export interface BypassDialogState {
  open: boolean;
  type: CapaBypassComplaintType | null;
  targetMessageId: string;
}

const BypassWarningBubble: React.FC<Props> = ({ message }) => {
  const targetMessageId =
    (message?.message as CapaQuotationBypassMessage).bypass?.id ?? '';
  const [bypassDialogState, setBypassDialogState] = useState<BypassDialogState>(
    {
      open: false,
      type: null,
      targetMessageId
    }
  );
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [updateBypassComplaint] = useUpdateBypassChatComplaintMutation();
  const [deleteChatMessage] = useDeleteQuotationChatMessageMutation();

  const onSelectNotAContact = async () => {
    try {
      // 바이패스 경고 버블 액션 DB에 저장
      const bypassComplaintResult = await updateBypassComplaint({
        variables: {
          input: {
            chatMessageId: (message?.message as CapaQuotationBypassMessage)
              .bypass?.id as string,
            complaintType: CapaBypassComplaintType.NotExchangeCallNumber
          }
        }
      });

      if (
        bypassComplaintResult.data?.updateBypassChatComplaint.result !==
        CapaErrorCode.Success
      ) {
        throw new Error(
          `update bypass complaint failed with ${bypassComplaintResult.data?.updateBypassChatComplaint.result}`
        );
      }

      // Bypass 경고 버블 삭제
      const deleteChatMessageResult = await deleteChatMessage({
        variables: {
          input: {
            deleteTargetMsgId: message?.id as string
          }
        }
      });

      if (
        deleteChatMessageResult.data?.deleteQuotationChatMessage.result !==
        CapaErrorCode.Success
      ) {
        throw new Error(
          `delete bypass bubble failed with ${bypassComplaintResult.data?.updateBypassChatComplaint.result}`
        );
      }
    } catch (e) {
      enqueueSnackbar(errorMessage.notBypass);
    }
  };

  return (
    <Box className={classes.root}>
      <BypassDialog
        dialogState={bypassDialogState}
        onClose={() => {
          setBypassDialogState((prev) => ({
            ...prev,
            open: false,
            type: null
          }));
        }}
      />
      <div className={classes.content}>
        <div className="header">
          <CustomizedTypography variant="body2" fontWeight="700" color="error">
            연락처 교환 주의!
          </CustomizedTypography>
        </div>
        <div>
          <CustomizedTypography
            variant="body2"
            className={classes.wordBreak}
            color="error"
          >
            개별적으로 연락처를 교환하여 캐파 외부에서 거래할 경우 분쟁 발생 시
            책임소재를 밝히기가 어렵습니다.
          </CustomizedTypography>
          <br />
          <CustomizedTypography
            variant="body2"
            className={classes.wordBreak}
            color="textPrimary"
          >
            개별적으로 연락을 취하려는 이유를 알려주시면 서비스 개선에
            반영하겠습니다.
          </CustomizedTypography>
        </div>
      </div>
      <div className={classes.buttonContainer}>
        <CustomizedButton
          color="default"
          variant="contained"
          fullWidth
          onClick={() => {
            setBypassDialogState((prev) => ({
              ...prev,
              open: true,
              type: CapaBypassComplaintType.ChatComplaint
            }));
          }}
        >
          캐파 채팅이 불편해요
        </CustomizedButton>
        <CustomizedButton
          color="default"
          variant="contained"
          fullWidth
          onClick={() => {
            setBypassDialogState((prev) => ({
              ...prev,
              open: true,
              type: CapaBypassComplaintType.RequiredCallNumber
            }));
          }}
        >
          통화로 해야해요
        </CustomizedButton>
        <CustomizedButton
          color="default"
          variant="contained"
          fullWidth
          onClick={onSelectNotAContact}
        >
          연락처 교환이 아니에요
        </CustomizedButton>
      </div>
    </Box>
  );
};

export default BypassWarningBubble;
